import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Network, Copy, Check, KeyRound, Building2, Users, Shield } from "lucide-react";

const fmt = (iso, lang) => { try { return iso ? new Date(iso).toLocaleString(lang === "de" ? "de-DE" : "en-US") : "—"; } catch { return iso || "—"; } };

// Settings → License: "Managed by MSP portal" status + an admin claim-code generator (path 2 adoption).
export const PortalConnectionCard = () => {
  const { t, lang } = useLang();
  const { user, demoMode } = useAuth();
  const isAdmin = user && ["admin", "superadmin"].includes(user.role);
  const [status, setStatus] = useState(null);
  const [claim, setClaim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [togglingSeat, setTogglingSeat] = useState(false);

  const load = () => api.get("/license/portal-status").then((r) => setStatus(r.data)).catch(() => setStatus({ managed: false }));
  useEffect(() => {
    load();
    if (isAdmin) api.get("/license/claim-code").then((r) => setClaim(r.data?.code ? r.data : null)).catch(() => {});
  }, [isAdmin]);

  const toggleSeat = async (enabled) => {
    setTogglingSeat(true);
    try {
      const { data } = await api.put("/license/portal-seat-reporting", { enabled });
      setStatus(data);
      toast.success(enabled ? t("portal.seatResumed") : t("portal.seatPaused"));
    } catch (e) { toast.error(apiErr(e)); } finally { setTogglingSeat(false); }
  };

  const genCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/license/claim-code");
      setClaim(data); setCopied(false);
      toast.success(t("portal.codeGenerated"));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(claim.code); setCopied(true); toast.success(t("portal.copied")); }
    catch { toast.error(t("cmdact.pwCopyFail")); }
  };

  if (!status) return (
    <section className="border border-zinc-200 bg-white p-5" data-testid="portal-connection-loading">
      <div className="h-4 w-40 animate-pulse rounded bg-zinc-100" />
    </section>
  );
  const managed = !!status.managed;

  return (
    <section className="border border-zinc-200 bg-white" data-testid="portal-connection-card">
      <div className="flex items-center gap-2 border-b border-zinc-100 p-5 text-sm font-bold text-zinc-900">
        <Network size={16} className="text-primary" /> {t("portal.title")}
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${managed ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}
          data-testid="portal-connection-badge">
          {managed ? t("portal.connected") : t("portal.notConnected")}
        </span>
      </div>

      <div className="space-y-4 p-5">
        {managed ? (
          <div className="space-y-3" data-testid="portal-managed-details">
            <p className="flex items-start gap-2 text-sm text-zinc-600">
              <Building2 size={15} className="mt-0.5 shrink-0 text-primary" />
              {t("portal.managedIntro")}
            </p>
            <dl className="grid grid-cols-1 gap-y-1.5 text-sm sm:grid-cols-2 sm:gap-x-6">
              <div className="flex justify-between gap-3"><dt className="text-zinc-400">{t("portal.partner")}</dt><dd className="font-mono text-xs text-zinc-700" data-testid="portal-msp-id">{status.mspId || "—"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-zinc-400">{t("portal.tenantRef")}</dt><dd className="font-mono text-xs text-zinc-700" data-testid="portal-tenant-ref">{status.tenantRef || "—"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-zinc-400">{t("portal.portalUrl")}</dt><dd className="truncate font-mono text-xs text-zinc-700">{status.portalBaseUrl || "—"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-zinc-400">{t("portal.issuer")}</dt><dd className="font-mono text-xs text-zinc-700">{status.issuer || "—"}</dd></div>
              <div className="flex justify-between gap-3 sm:col-span-2"><dt className="text-zinc-400">{t("portal.audience")}</dt><dd className="truncate font-mono text-xs text-zinc-700">{status.installationUrl || "—"}</dd></div>
              <div className="flex justify-between gap-3 sm:col-span-2"><dt className="text-zinc-400">{t("portal.jwksUrl")}</dt><dd className="truncate font-mono text-xs text-zinc-700" data-testid="portal-jwks-url">{status.jwksUrl || "—"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-zinc-400">{t("portal.lastSso")}</dt><dd className="text-xs font-medium text-zinc-700" data-testid="portal-last-sso">{status.lastSso?.at ? `${status.lastSso.email || ""} · ${fmt(status.lastSso.at, lang)}` : t("portal.never")}</dd></div>
            </dl>

            <div className="flex items-center justify-between gap-3 rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2.5" data-testid="portal-seat-toggle-row">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-zinc-800">{t("portal.seatReporting")}</p>
                <p className="text-[11px] text-zinc-500">
                  {status.seatReportingConfigured
                    ? (status.seatReportingPaused ? t("portal.seatPausedHint") : t("portal.seatOnHint"))
                    : t("portal.seatOff")}
                  {status.lastSeatReport?.at ? ` · ${t("portal.lastReport")} ${fmt(status.lastSeatReport.at, lang)}` : ""}
                </p>
              </div>
              {isAdmin && status.seatReportingConfigured && (
                <Switch checked={!status.seatReportingPaused} disabled={togglingSeat}
                  onCheckedChange={(v) => toggleSeat(v)} data-testid="portal-seat-toggle" />
              )}
            </div>

            {(status.seatHistory || []).length > 0 && (
              <div data-testid="portal-seat-history">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-zinc-400">{t("portal.seatHistory")}</p>
                <ul className="divide-y divide-zinc-100 rounded-sm border border-zinc-200">
                  {status.seatHistory.map((h, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs" data-testid={`portal-seat-history-row-${i}`}>
                      <span className="text-zinc-500">{fmt(h.at, lang)}</span>
                      <span className="flex items-center gap-2 font-mono text-zinc-600">
                        <span className="inline-flex items-center gap-1" title={t("portal.usersLabel")} aria-label={`${h.users} ${t("portal.usersLabel")}`}><Users size={12} className="text-zinc-400" />{h.users}</span>
                        <span className="inline-flex items-center gap-1" title={t("portal.adminsLabel")} aria-label={`${h.admins} ${t("portal.adminsLabel")}`}><Shield size={12} className="text-zinc-400" />{h.admins}</span>
                      </span>
                      <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase ${h.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {h.ok ? t("portal.reportOk") : t("portal.reportFail")}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="portal-adopt-block">
            <p className="text-sm text-zinc-600">{t("portal.adoptHint")}</p>
            {isAdmin && !demoMode && (
              <>
                {claim?.code ? (
                  <div className="space-y-2 rounded-sm border border-indigo-200 bg-indigo-50/50 p-3">
                    <div className="flex items-center gap-2">
                      <code className="flex-1 select-all rounded-sm border border-indigo-200 bg-white px-3 py-2 font-mono text-sm tracking-wider text-indigo-900" data-testid="portal-claim-code">{claim.code}</code>
                      <Button onClick={copyCode} data-testid="portal-claim-copy" className="h-9 shrink-0 rounded-sm bg-primary text-primary-foreground hover:bg-[#002480]">
                        {copied ? <Check size={15} className="mr-1.5" /> : <Copy size={15} className="mr-1.5" />} {copied ? t("portal.copied") : t("portal.copy")}
                      </Button>
                    </div>
                    <p className="text-[11px] text-indigo-700" data-testid="portal-claim-expires">{t("portal.codeExpires").replace("{d}", fmt(claim.expiresAt, lang))}</p>
                    <Button variant="outline" onClick={genCode} disabled={busy} data-testid="portal-claim-regenerate" className="h-8 rounded-sm border-zinc-300">{t("portal.regenerate")}</Button>
                  </div>
                ) : (
                  <Button onClick={genCode} disabled={busy} data-testid="portal-generate-claim" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#002480]">
                    <KeyRound size={15} className="mr-1.5" /> {busy ? t("portal.generating") : t("portal.generateCode")}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
